/**
 * @fileoverview Introspect function for token verification.
 */

import { apiRequest } from "../../../core/api";
import { logger } from "../../../core/logger";
import { getEmbeddedApp } from "../../../core/EmbeddedApp";
import { postToHost } from "../../../core/messenger";
import { AUTH_EVENTS } from "../events";
import type { IntrospectResponse, IntrospectResponseData } from "../types";

/**
 * Raw API response from the introspect endpoint.
 */
interface RawIntrospectApiResponse {
  status: number;
  success: boolean;
  data: IntrospectResponseData;
}

/**
 * Options for the introspect function.
 */
export interface IntrospectParams {
  /** Application ID (audience) */
  appId: string;
  /** Short-lived token */
  token: string;
  /** Automatically refresh token on error (default: true) */
  refreshOnError?: boolean;
}

/**
 * Create error response for introspect.
 */
function createErrorResponse(errorMessage: unknown): IntrospectResponse {
  return {
    isVerified: false,
    isError: true,
    error: errorMessage,
    data: null,
  };
}

/**
 * Introspect (verify) a short-lived token with Salla's API.
 * This function verifies the token and returns token information.
 *
 * @param params - Introspect parameters including token, appId, and refreshOnError
 * @returns Promise that resolves with the introspect response
 */
export async function introspect(
  params: IntrospectParams,
): Promise<IntrospectResponse> {
  const { token, appId, refreshOnError = true } = params;

  if (!token) {
    const errorMessage =
      "Token is required. Provide it as a parameter or in URL as ?token=XXX";
    logger.error("Error in introspect:", errorMessage);
    return createErrorResponse(errorMessage);
  }

  if (!appId) {
    const errorMessage =
      "App ID is required. Provide it as a parameter or in URL as ?app_id=XXX";
    logger.error("Error in introspect:", errorMessage);
    return createErrorResponse(errorMessage);
  }

  try {
    const apiResponse = await apiRequest<RawIntrospectApiResponse>(
      "/exchange-authority/v1/introspect",
      {
        method: "POST",
        headers: {
          "S-Source": appId,
          "Content-Type": "application/json",
        },
        body: {
          env: "prod",
          token,
          iss: "merchant-dashboard",
          subject: "embedded-page",
        },
      },
    );

    // Transform API response to SDK response format
    const isVerified = apiResponse.success;
    return {
      isVerified,
      isError: !isVerified,
      error: !isVerified ? "API request failed" : undefined,
      data: isVerified ? apiResponse.data : null,
    };
  } catch (error) {
    // Default error handler: refresh token if refreshOnError is enabled
    if (refreshOnError) {
      getEmbeddedApp().ui.toast.error(error?.toString() ?? "Introspect error");
      postToHost(AUTH_EVENTS.REFRESH, {});
    }
    logger.error("Error in introspect:", error);

    // Resolve with error response instead of rejecting
    const errorMessage =
      error instanceof Error
        ? error.message
        : typeof error === "string"
          ? error
          : error;

    return createErrorResponse(errorMessage);
  }
}
